import _ from 'lodash'
import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type {
	ViewerModel,
	BrowserWindow } from '@wix/thunderbolt-symbols'
import {
	CurrentRouteInfoSymbol,
	MasterPageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	ViewerModelSym,
	BrowserWindowSymbol,
} from '@wix/thunderbolt-symbols'
import type {
	ICurrentRouteInfo,
	IRoutingConfig,
	IRoutingLinkUtilsAPI,
	RouterMasterPageConfig,
} from './types'
import { name } from './symbols'
import { resolveUrl } from './resolveUrl'
import { getRelativeUrl } from '@wix/url-manager'

export const RoutingLinkUtilsAPI = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		CurrentRouteInfoSymbol,
		named(MasterPageFeatureConfigSymbol, name),
		ViewerModelSym,
		BrowserWindowSymbol
	],
	(
		routingConfig: IRoutingConfig,
		currentRouteInfo: ICurrentRouteInfo,
		masterPageConfig: RouterMasterPageConfig,
		viewerModel: ViewerModel,
		browserWindow: BrowserWindow,
	): IRoutingLinkUtilsAPI => {
		/**
		These are methods of the UrlHistoryManager, but we don't want to use it directly because it creates the following circular dependency:
		Services Manager -> WixLinkUtilsService -> RoutingLinkUtilsAPI  -> UrlHistoryManager -> Services Manager
		This is a workaround to avoid the circular dependency.
		 */
		const getCurrentUrl = (): string => browserWindow?.location.href || viewerModel.requestUrl
		const getParsedUrl = () => new URL(getCurrentUrl())

		return {
			getLinkUtilsRoutingInfo() {
				const routeInfo =
					currentRouteInfo.getCurrentRouteInfo() ||
					resolveUrl(getParsedUrl().href, routingConfig)
				const { pageId } = routeInfo

				const isCustomizedUrl = routingConfig.urlMappingsExist
				const { mainPageId, pagesMap, routes, pageIdToPrefix, baseUrl } = routingConfig
				return {
					mainPageId,
					pages: pagesMap,
					routes: _.omitBy(routes, (__, key) => key === './'),
					pageIdToPrefix,
					pageId: pageId!,
					relativeUrl: getRelativeUrl(getCurrentUrl(), baseUrl),
					externalBaseUrl: baseUrl,
					pagesUriSEOs: masterPageConfig.pagesUriSeoML.primaryToCurrentLang,
					isCustomizedUrl,
				}
			},
		}
	}
)
